import { CurrencyPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TeamInterface } from 'src/app/interfaces/team/team.interface';
import { BrandsService } from 'src/app/services/brands/brands.service';
import { TeamMember } from '../../interfaces/team/member/member.interface';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { StripeService } from '../../services/stripe/stripe.service';
import { TeamsService } from '../../services/teams/teams.service';
import { TeamMemberRemovalCoordinator } from '../../services/team-member-removal/team-member-removal-coordinator.service';
import { UiAlertService } from '../../services/ui-alert/ui-alert.service';
import { UsersService } from '../../services/users/users.service';
import { CustomMonthlyAllowanceComponent } from '../modal/custommonthlyallowance/custommonthlyallowance.component';
import {
  RefundAllowanceDialogComponent,
  RefundAllowanceDialogModel,
} from '../modal/refundallowancedialog/refundallowancedialog.component';
import { SnackBarComponent } from '../snackbar/snackbar.component';
import { MemberProductEligibilityDialogComponent } from '../modal/member-product-eligibility-dialog/member-product-eligibility-dialog.component';

@Component({
    selector: 'app-user-item',
    templateUrl: './user-item.component.html',
    styleUrls: ['./user-item.component.scss'],
    providers: [CurrencyPipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserItemComponent implements OnInit, AfterViewInit {
  private hasAvatar = false;

  @Input() member!: TeamMember;
  @Input() detail = false;
  @Input() team!: TeamInterface;
  @Input() showHeader = false;
  @Input() wallitBalance = false;
  @Input() readOnly = false;
  @Input() allowRestore = false;
  restoringMember = false;
  @ViewChild('avatar') avatar: any;
  @ViewChild('item') item: any;
  @Output() memberChangedEvent: EventEmitter<any> = new EventEmitter<any>();
  hasPendingACHCount = false;
  public newMembers: Array<any> = [];
  public members: Array<any> = [];
  public paymentMethod: any;
  constructor(
    private router: Router,
    private usersService: UsersService,
    private uiAlertService: UiAlertService,
    private teamsService: TeamsService,
    private snackBar: MatSnackBar,
    private stripeService: StripeService,
    private spinnerService: SpinnerService,
    private dialog: MatDialog,
    private currencyPipe: CurrencyPipe,
    private readonly brandsService: BrandsService,
    private readonly teamMemberRemovalCoordinator: TeamMemberRemovalCoordinator,
  ) {}

  ngOnInit(): void {}

  async ngAfterViewInit(): Promise<void> {
    this.hasAvatar = this.usersService.getAvatar(this.member, this.avatar.nativeElement);
    this.item.detail = this.detail;
    const teamPaymentMethod = await this.teamsService.getPaymentMethod(this.team);
    const paymentMethods = await this.teamsService.getPaymentMethods(this.team.id);
    this.paymentMethod = paymentMethods.find(
      (thisMethod: { stripePaymentMethod: string }) =>
        thisMethod.stripePaymentMethod === teamPaymentMethod,
    );
  }

  canSendOneTimeRewards(): boolean | undefined {
    const brandConfig = this.brandsService.getConfigJSON();
    // TODO: When getConfigJSON is fixed, remove comment
    const brandHaveAccounts = true; // !!brandConfig.content?.ui?.tabs?.accounts;
    if (this.member.disabled || this.isDeactivationPendingSettlement(this.member)) {
      return false;
    }
    const validPaymentMethod = !!(this.paymentMethod && !this.paymentMethod?.bankInfo?.isPending);
    const isMemberSignedUp = !!this.member.userId;
    const canSendUsingWallit =
      isMemberSignedUp && brandHaveAccounts && this.member.userId !== this.team.ownerUserId;
    // team.disabled pauses reimbursements only, not rewards.
    const canSend =
      this.team.approved &&
      (canSendUsingWallit || validPaymentMethod);
    return canSend;
  }

  mountTooltipMessage(): string {
    const brandConfig = this.brandsService.getConfigJSON();
    // TODO: When getConfigJSON is fixed, remove comment
    const brandHaveAccounts = true; // !!brandConfig.content?.ui?.tabs?.accounts;
    const isMemberSignedUp = !!this.member.userId;
    const canSendUsingWallit =
      isMemberSignedUp && brandHaveAccounts && this.member.userId !== this.team.ownerUserId;
    let message = '';
    if ((!this.paymentMethod || this.paymentMethod.bankInfo?.isPending) && !canSendUsingWallit) {
      message += 'The team must have a payment method';
    }
    message += ' to send one-time rewards';
    return message;
  }

  getMemberName(member: TeamMember): string {
    const firstName = member.firstName || member.teamuserMetadata?.firstName || '';
    const lastName = member.lastName || member.teamuserMetadata?.lastName || '';
    return `${firstName} ${lastName}`.trim();
  }

  getEmail(member: TeamMember): string {
    return member.email || member.userEmail;
  }

  getLoginName(member: TeamMember): string {
    return member.loginName ? '@' + member.loginName : '';
  }

  getInvitationLabel(member: TeamMember): string {
    if (member.inviteAcceptedAt) {
      return 'Accepted';
    }
    if (member.inviteSentAt) {
      return 'Invitation Sent';
    }
    return 'Not Sent';
  }

  getInvitationClass(member: TeamMember): string {
    if (member.inviteAcceptedAt) {
      return 'invitation-accepted';
    }
    if (member.inviteSentAt) {
      return 'invitation-sent';
    }
    return 'invitation-not-sent';
  }

  showUserDetail(event: any, user: any): void {
    event.stopPropagation();
    if (user.userId) {
      this.router.navigate(['/userdetail', user.userId]);
    }
  }

  showPendingAchSnack(snackMessage: string): void {
    this.spinnerService.hide();
    this.snackBar.openFromComponent(SnackBarComponent, {
      data: snackMessage,
    });
  }

  isDeactivationPendingSettlement(member: TeamMember): boolean {
    return this.teamMemberRemovalCoordinator.isDeactivationPendingSettlement(member);
  }

  getMemberStatusLabel(member: TeamMember): string {
    return this.isRemovedMember(member)
      ? 'Removed'
      : this.isDeactivationPendingSettlement(member)
      ? 'Deactivation pending settlement'
      : member.message || '---';
  }

  isRemovedMember(member: TeamMember): boolean {
    const isTruthyFlag = (value: unknown): boolean =>
      value === true || value === 1 || value === '1' || value === 'true';
    return isTruthyFlag(member?.teamuserMetadata?.deleted)
      || !!member?.memberDeletedAt;
  }

  getAllowanceRefundReservation(member: TeamMember): any {
    return member?.teamuserMetadata?.allowanceRefundReservation || null;
  }

  getReservedRefundAmount(member: TeamMember): string {
    return (Number(this.getAllowanceRefundReservation(member)?.amountCents || 0) / 100).toFixed(2);
  }

  private applyAllowanceRefundResult(member: TeamMember, result: any): void {
    member.balance = result.balance;
    member.teamuserMetadata = member.teamuserMetadata || {};
    if (result.status === 'AWAITING_LIQUIDITY') {
      member.teamuserMetadata.allowanceRefundReservation = {
        requestId: result.requestId,
        amountCents: Math.round(Number(result.refundedAmount) * 100),
        status: result.status,
      };
    } else {
      delete member.teamuserMetadata.allowanceRefundReservation;
    }
    this.memberChanged();
  }

  private getAllowanceRefundErrorMessage(error: any, fallbackMessage: string): string {
    if (error?.status === 0) {
      return 'Refund status could not be confirmed. Refresh the team member before trying again.';
    }
    if (error?.data?.code === 'PENDING_ACH_TRANSFERS') {
      return 'Allowance refunds are unavailable while this wellspace has pending ACH funding. Please wait for funding to settle.';
    }
    if (typeof error?.data?.error === 'string' && error.data.error.trim()) {
      return error.data.message || error.data.error;
    }
    return error?.data?.message || error?.message || fallbackMessage;
  }

  private showAllowanceRefundStatus(member: TeamMember): void {
    const dialogRef = this.dialog.open(RefundAllowanceDialogComponent, {
      data: new RefundAllowanceDialogModel(member),
    });
    dialogRef.afterClosed().subscribe(async (action) => {
      const reservation = this.getAllowanceRefundReservation(member);
      if (!reservation || !['retry', 'cancel'].includes(action)) {
        return;
      }
      if (action === 'cancel') {
        const confirmed = await this.uiAlertService.presentAlertConfirm(
          'Cancel this refund request and return the reserved amount to the member allowance?',
        );
        if (!confirmed) {
          return;
        }
      }
      this.spinnerService.show(action === 'retry' ? 'Retrying allowance refund...' : 'Cancelling allowance refund...');
      try {
        const result = action === 'retry'
          ? await this.teamsService.retryTeamMemberAllowanceRefund(
            this.team.id,
            member.memberId,
            reservation.requestId,
          )
          : await this.teamsService.cancelTeamMemberAllowanceRefund(
            this.team.id,
            member.memberId,
            reservation.requestId,
          );
        this.applyAllowanceRefundResult(member, result);
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: result.message || (result.status === 'COMPLETED'
            ? `$${result.refundedAmount} has been refunded`
            : 'Allowance refund request updated'),
        });
      } catch (error: any) {
        this.memberChangedEvent.emit(true);
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: this.getAllowanceRefundErrorMessage(error, 'Unable to update allowance refund'),
        });
      } finally {
        this.spinnerService.hide();
      }
    });
  }

  async refundMember(member: TeamMember): Promise<void> {
    if (this.getAllowanceRefundReservation(member)) {
      this.showAllowanceRefundStatus(member);
      return;
    }
    const amountString = parseFloat(member.balance).toFixed(2);
    if (amountString === '0.00') {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: `This member has a zero allowance balance so there is nothing to refund`,
      });
      return;
    }
    const dialogRef = this.dialog.open(RefundAllowanceDialogComponent, {
      data: new RefundAllowanceDialogModel(member),
    });
    dialogRef.afterClosed().subscribe((dialogResult) => {
      if (dialogResult !== null && dialogResult !== undefined) {
        const refundAmount = Number(dialogResult);
        const formattedRefundAmount = refundAmount.toFixed(2);
        this.uiAlertService
          .presentAlertConfirm(
            `$${formattedRefundAmount} will be refunded through Stripe against eligible prior charges. Do you really want to refund from this team member's allowance?`,
          )
          .then(async (confirm) => {
            if (confirm) {
              this.spinnerService.show('Refunding allowance...');
              try {
                const result = await this.teamsService.refundTeamMemberAllowance(
                  this.team.id,
                  this.member.memberId,
                  refundAmount,
                );
                this.applyAllowanceRefundResult(member, result);
                this.snackBar.openFromComponent(SnackBarComponent, {
                  data: result.message || `$${formattedRefundAmount} has been refunded`,
                });
              } catch (error: any) {
                this.snackBar.openFromComponent(SnackBarComponent, {
                  data: this.getAllowanceRefundErrorMessage(error, 'Unable to refund allowance'),
                });
              } finally {
                this.spinnerService.hide();
              }
            }
          });
      }
    });
  }

  async restoreMember(member: TeamMember): Promise<void> {
    if (!this.allowRestore || !this.isRemovedMember(member) || this.restoringMember) return;
    this.restoringMember = true;
    try {
      const confirmed = await this.uiAlertService.presentAlertConfirm(
        'Restore this member’s Wellspace membership? No new invitation will be sent, and previous acceptance will be preserved. '
        + 'Monthly allowances and employer-paid benefits will not restart automatically. '
        + 'Reset the allowance and arrange any required employer-paid policy re-enrollment manually. '
        + 'An invitation that was never accepted will remain unaccepted.',
      );
      if (!confirmed) return;
      await this.teamsService.restoreTeamMember(member.memberId, this.team.id);
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Member restored. Reset allowances and arrange employer-paid benefit re-enrollment manually.',
      });
      this.memberChangedEvent.emit(true);
    } catch (error: any) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: error?.error?.error || error?.data?.error || 'Unable to restore member.',
      });
    } finally {
      this.restoringMember = false;
    }
  }

  async deleteMember(member: TeamMember, dontDelete: boolean = false): Promise<void> {
    const result = await this.teamMemberRemovalCoordinator.removeMember({
      team: this.team,
      member,
      dontDelete,
    });
    if (result.status === 'succeeded') {
      this.memberChangedEvent.emit(true);
    }
  }

  resendMember(member: TeamMember): void {
    this.teamsService.resendEmailToTeamMember(member.memberId, this.team.id).then((response) => {
      member.message = 'Email Resent';
      this.snackBar.openFromComponent(SnackBarComponent, { data: 'Email has been sent again' });
    });
  }

  private async hasFundingSource(): Promise<any> {
    const hasSource = await this.teamsService.getPaymentMethod(this.team);
    if (!hasSource) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'You need to set up a payment method before sending rewards',
      });
    }
    return hasSource !== null;
  }

  async setCustomMonthlyAllowance(member: TeamMember): Promise<void> {
    if (this.isDeactivationPendingSettlement(member)) {
      return;
    }
    const dialogRef = this.dialog.open(CustomMonthlyAllowanceComponent, {
      data: { member, teamAmount: this.team.amount },
    });
    dialogRef.afterClosed().subscribe((dialogResult: number | null) => {
      if (dialogResult) {
        this.spinnerService.show('Saving...');
        this.usersService
          .setCustomMonthlyAllowance(this.team.id, member.email, dialogResult)
          .then(() => {
            this.spinnerService.hide();
            this.snackBar.openFromComponent(SnackBarComponent, {
              data: 'Member allowance amount changed',
            });
            this.memberChangedEvent.emit(true);
          })
          .catch(() => {
            this.spinnerService.hide();
            this.snackBar.openFromComponent(SnackBarComponent, {
              data: 'Failed to change member allowance amount',
            });
          });
      }
    });
  }

  setProductEligibility(member: TeamMember): void {
    const dialogRef = this.dialog.open(MemberProductEligibilityDialogComponent, {
      data: { member, team: this.team },
      width: '480px',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      const count = Array.isArray(result.productIds) ? result.productIds.length : 0;
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: `Product eligibility saved for ${count} product${count === 1 ? '' : 's'}`,
      });
      this.memberChangedEvent.emit(true);
    });
  }

  getMonthlyAllowanceLabel(member: TeamMember): string {
    if (member.canReceiveAllowance) {
      return member.monthlyAllowance
        ? (this.currencyPipe.transform(member.monthlyAllowance) as string)
        : 'Default';
    }
    return '---';
  }

  async rewardMember(member: TeamMember): Promise<any> {
    if (this.isDeactivationPendingSettlement(member)) {
      return;
    }
    this.uiAlertService.memberOneTimeReward(member, false).then((_) => {
      setTimeout(() => {
        this.memberChangedEvent.emit(true);
      }, 1000);
    });
  }

  async p2pTopupMember(member: TeamMember): Promise<any> {
    if (!member.userId) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'This member must be signed up before sending peer-to-peer top-ups',
      });
      return;
    }
    if (await this.hasFundingSource()) {
      this.uiAlertService.memberOneTimeReward(member, true).then((result) => {
        this.member.balance = (parseFloat(this.member.balance as string) + result).toFixed(2);
        this.member = JSON.parse(JSON.stringify(this.member));
      });
    }
  }

  private memberChanged(): void {
    this.member = JSON.parse(JSON.stringify(this.member));
  }

  changeEmploymentDate(member: TeamMember): void {
    this.uiAlertService
      .presentDateInput(
        'Change Employment Date',
        'Employment Date',
        member.employmentDate ?? undefined,
      )
      .then((result) => {
        if (result) {
          this.teamsService
            .modifyTeamMember(this.team.id, member.memberId, { employmentDate: result })
            .then(() => {
              this.snackBar.openFromComponent(SnackBarComponent, {
                data: 'Member employment date changed',
              });
              this.member.employmentDate = result;
              this.memberChanged();
            });
        }
      });
  }

  changeBirthDate(member: TeamMember): void {
    this.uiAlertService
        .presentDateInput(
            'Change Birth Date',
            'Birth Date',
            member.teamuserMetadata?.birthDate ?? undefined,
        )
        .then((result) => {
          if (result) {
            const resolvedDate: unknown = result;
            const birthDate = resolvedDate instanceof Date
              ? `${resolvedDate.getFullYear()}-${String(resolvedDate.getMonth() + 1).padStart(2, '0')}-${String(resolvedDate.getDate()).padStart(2, '0')}`
              : result;
            const teamuserMetadata = this.member.teamuserMetadata
              && typeof this.member.teamuserMetadata === 'object'
              ? { ...this.member.teamuserMetadata }
              : {};
            teamuserMetadata.birthDate = birthDate;
            this.teamsService
                .modifyTeamMember(this.team.id, member.memberId, { metadata: teamuserMetadata })
                .then(() => {
                  this.snackBar.openFromComponent(SnackBarComponent, {
                    data: 'Member birth date changed',
                  });
                  if (!this.member.teamuserMetadata) {
                    this.member.teamuserMetadata = {};
                  }
                  this.member.teamuserMetadata.birthDate = birthDate;
                  this.memberChanged();
                });
          }
        });
  }

  changeEmployeeNumber(member: TeamMember): void {
    this.uiAlertService
      .presentFieldInput(
        'Change Employee Number/ID',
        'Employee Number/ID',
        member.employeeNumber ?? undefined,
      )
      .then((result) => {
        if (result) {
          this.teamsService
            .modifyTeamMember(this.team.id, member.memberId, { employeeNumber: result })
            .then(() => {
              this.snackBar.openFromComponent(SnackBarComponent, {
                data: 'Member employee number/ID changed',
              });
              this.member.employeeNumber = result;
              this.memberChanged();
            });
        }
      });
  }

  toggleAdmin(member: TeamMember): void {
    const isAdmin = member.role === 'admin';
    this.member.role = isAdmin ? '' : 'admin';
    this.teamsService
      .modifyTeamMember(this.team.id, member.memberId, { role: this.member.role })
      .then(() => {
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: isAdmin ? 'Admin rights removed' : 'Admin rights added',
        });
        this.memberChanged();
      });
  }

  enableOrDisableUserMonthlyAllowance(user: any, monthAllowanceValue: 0 | 1): void {
    this.spinnerService.show('Processing');
    this.usersService
      .enableOrDisableUserMonthlyAllowance(this.team.id, user.userEmail, monthAllowanceValue)
      .then((): void => {
        this.member.canReceiveAllowance = monthAllowanceValue;
        this.memberChanged();
        this.teamsService.setWellspace(this.team);
        this.spinnerService.hide();
        this.snackBar.openFromComponent(SnackBarComponent, {
          data:
            user && user.canReceiveAllowance === 1
              ? 'Enabled monthly allowance'
              : 'Disabled monthly allowance',
        });
      })
      .catch((err) => {
        console.error('Error in change user monthly allowance', err);
        this.spinnerService.hide();
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: 'Failed to disable member allowance',
        });
      });
  }

  async changeUserMonthlyAllowance(user: any): Promise<void> {
    const monthAllowanceValue: 0 | 1 = user && user.canReceiveAllowance === 1 ? 0 : 1;
    let message = '';
    if (monthAllowanceValue === 0) {
      message = `Turning off a member's allowance will affect your subscription plan for the upcoming month if they've already received their allowance. Would you like to proceed?`;
    } else {
      message = `Would you like to enable the member ${user.firstName ?? ''}'s allowance?`;
    }
    const ok = await this.uiAlertService.presentAlertConfirm(message);
    if (ok) {
      this.enableOrDisableUserMonthlyAllowance(user, monthAllowanceValue);
    }
  }
}
